import { useCallback, useMemo, useState } from "react";
import {
  type Assistance,
  Gender,
  type Passport,
  type Person,
} from "@b2bportal/air-booking-api";
import { I18nMarkup, useI18nContext, useI18nCountry } from "@hopper-b2b/i18n";
import {
  ClientName,
  type INationality,
  type LocalDate,
  type PersonId,
  type RadioOption,
  TranslationLanguage,
  type UserInfo,
} from "@hopper-b2b/types";
import {
  ActionButton,
  B2BTextField,
  MobileFloatingSection,
  Slot,
} from "@hopper-b2b/ui";
import {
  generateUserId,
  getDateTimeWithFormat,
  getEnvVariables,
  getNationalIdInfo,
  nameRegex,
  parseDateWithFormatting,
  parseDayMonthAndYear,
  parseMonthDayAndYear,
  passportRegex,
  specialCharacterRegex,
  toGender,
} from "@hopper-b2b/utilities";
import { Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import clsx from "clsx";
import dayjs from "dayjs";
import emojiFlags from "emoji-flags";

import { isEmpty } from "lodash-es";
import {
  AdditionalInformationSection,
  FrequentFlyerSection,
  HotelLoyaltySection,
} from "./components";
import { DriverLicenseSection } from "./components/DriverLicenseSection";
import "./styles.scss";
import { TextField } from "./components/TextField";
import {
  genderOptions,
  getFrequentFlyerList,
  getFrequentFlyerMap,
  getHotelLoyaltyList,
  getHotelLoyaltyMap,
  type IFrequentFlyerList,
  type IHotelLoyaltyList,
  renderMenu,
} from "./utils";

export interface ITravelerInfoFormProps {
  className?: string;
  travelerInfoTitle: string;
  travelerInfoSubtitle: string;
  frequentFlyerTitle: string;
  additionalInfoTitle: string;
  passportTitle?: string;
  passportSubtitle?: string;
  traveler?: Person;
  handleUpdatePassenger: (
    traveler: Person,
    onUpdate?: boolean,
    singleTravelerWorkflow?: boolean
  ) => void;
  singleTravelerWorkflow?: boolean;
  handleRemovePassenger: (travelerId: PersonId) => void;
  isMobile?: boolean;
  closeButton?: JSX.Element;
  prefillPassenger?: UserInfo;
  showPassport?: boolean;
  hideFrequentFlyerSection?: boolean;
  hideHotelLoyaltySection?: boolean;
  hideNationalityField?: boolean;
  hideAdditionalInfoSection?: boolean;
  hideGenderField?: boolean;
  showDriverLicenseSection?: boolean;
  buttonClassName?: string;
  hideHeader?: boolean;
  isJetBlueTenant: boolean;
  minBirthdate?: Date;
  handleClose?: () => void;
  ctaEnabledByDefault?: boolean;
  nationalityRequired?: boolean;
  isInputFocused?: boolean;
  setIsInputFocused?: (val: boolean) => void;
  /**
   * Controls scroll behavior when an input is focused on mobile.
   * - "smooth": Animated scroll (default)
   * - "instant": Immediate scroll without animation
   * - "none": Disable scrollIntoView entirely (useful for iOS Safari iframe contexts)
   */
  scrollOnFocusBehavior?: "smooth" | "instant" | "none";
}

export const TravelerInfoForm = ({
  className,
  travelerInfoTitle,
  travelerInfoSubtitle,
  frequentFlyerTitle,
  additionalInfoTitle,
  passportTitle,
  passportSubtitle,
  traveler,
  handleUpdatePassenger,
  singleTravelerWorkflow,
  handleRemovePassenger,
  isMobile,
  closeButton,
  prefillPassenger,
  showPassport = false,
  hideFrequentFlyerSection = false,
  hideHotelLoyaltySection = true,
  hideNationalityField = false,
  hideGenderField = false,
  hideAdditionalInfoSection = false,
  showDriverLicenseSection,
  buttonClassName,
  hideHeader = false,
  isJetBlueTenant,
  handleClose,
  minBirthdate,
  ctaEnabledByDefault = false,
  nationalityRequired = false,
  isInputFocused,
  setIsInputFocused,
  scrollOnFocusBehavior = "smooth",
}: ITravelerInfoFormProps) => {
  const { t, brand, language } = useI18nContext();
  const { getAllCountryNames, getCountryName } = useI18nCountry();

  const resetIsInputFocused = () => {
    if (setIsInputFocused) {
      setIsInputFocused(false);
    }
  };

  // en is defined as US English
  const preferedDateFormat =
    language === TranslationLanguage.en ? "MM/DD/YYYY" : "DD/MM/YYYY";
  const preferMonthDayYearFormat = language === TranslationLanguage.en;

  const [saveClicked, setSaveClicked] = useState(false);

  // Traveler Info
  const [dirtyFields, setDirtyFields] = useState<string[]>([]);
  const [firstName, setFirstName] = useState(
    traveler?.givenName || prefillPassenger?.firstName || ""
  );
  const [middleName, setMiddleName] = useState(traveler?.middleName ?? "");
  const [lastName, setLastName] = useState(
    traveler?.surname || prefillPassenger?.lastName || ""
  );
  const [birthdate, setBirthdate] = useState<LocalDate>(
    traveler && traveler?.dateOfBirth
      ? dayjs(traveler.dateOfBirth).format(preferedDateFormat)
      : prefillPassenger?.birthDate
        ? dayjs(prefillPassenger.birthDate).format(preferedDateFormat)
        : ""
  );

  const [gender, setGender] = useState<Gender | null>(
    traveler?.gender || toGender(prefillPassenger?.gender) || null
  );
  const [nationality, setNationality] = useState<INationality | undefined>(
    traveler?.createdAt
      ? traveler?.nationality
      : { country: brand.preferredCountryCode }
  );

  const nationalIdInfo = useMemo(
    () => getNationalIdInfo(nationality?.country),
    [nationality]
  );

  const [nationalId, setNationalId] = useState(
    traveler?.nationalId
      ? {
          number: traveler?.nationalId?.number
            ? nationalIdInfo
              ? nationalIdInfo?.format(traveler?.nationalId?.number)
              : traveler?.nationalId?.number
            : "",
          ...(traveler?.nationalId?.expiration
            ? { expiration: traveler?.nationalId?.expiration }
            : null),
        }
      : null
  );

  // Frequent Flyer Details
  const [frequentFlyerList, setFrequentFlyerList] =
    useState<IFrequentFlyerList>(
      traveler?.frequentFlyer
        ? getFrequentFlyerList(traveler.frequentFlyer)
        : []
    );

  const [hotelLoyaltyList, setHotelLoyaltyList] = useState<IHotelLoyaltyList>(
    traveler?.hotelLoyalty ? getHotelLoyaltyList(traveler.hotelLoyalty) : []
  );

  // Additional Info
  const [assistance, setAssistance] = useState<Assistance[]>(
    traveler?.assistance ?? []
  );
  const [redressNumber, setRedressNumber] = useState(traveler?.redressNumber);
  const [knownTravelerNumber, setKnownTravelerNumber] = useState(
    traveler?.knownTravelerNumber
  );

  // Drivers licence
  const [driverLicense, setLicense] = useState({
    cityCode: traveler?.driverLicense?.cityCode ?? "",
    street: traveler?.driverLicense?.street ?? "",
    country:
      traveler?.driverLicense?.country ??
      traveler?.nationality?.country ??
      "US",
    postCode: traveler?.driverLicense?.postCode ?? "",
  });

  // Passport
  const [passport, setPassport] = useState<Passport>({
    countryOfIssue: traveler?.passport?.countryOfIssue || "",
    number: traveler?.passport?.number || "",
    expiration: traveler?.passport?.expiration
      ? dayjs(traveler.passport.expiration).format(preferedDateFormat)
      : "",
  });

  const isMissingDriverLicense =
    isEmpty(driverLicense.cityCode) ||
    isEmpty(driverLicense.postCode) ||
    isEmpty(driverLicense.country) ||
    isEmpty(driverLicense.street);

  const isNameInvalid = (name?: string) => name && !nameRegex.test(name);
  const isNameLongEnough = (name?: string) => name && name.length <= 1;
  const isNameTooLong = (name?: string) => name && name.length > 32;

  const genderError = useMemo(() => {
    if (saveClicked && !gender) {
      return t("genderRequiredError");
    }
  }, [gender, t, saveClicked]);

  const nationalityError = useMemo(() => {
    if (nationalityRequired && saveClicked && !nationality) {
      return t("nationalityRequiredError");
    }
  }, [nationality, nationalityRequired, t, saveClicked]);

  const firstNameError = useMemo(() => {
    if (
      (!firstName || firstName === "") &&
      (saveClicked || dirtyFields.includes("firstName"))
    ) {
      return t("firstNameRequiredError");
    } else if (isNameLongEnough(firstName)) {
      return t("firstNameMinCharacterError");
    } else if (isNameInvalid(firstName)) {
      return t("noSpecialCharactersError");
    } else if (isNameTooLong(firstName)) {
      return t("firstNameMaxCharacterError");
    }
  }, [firstName, t, dirtyFields, saveClicked]);

  const lastNameError = useMemo(() => {
    if (
      (!lastName || lastName === "") &&
      (saveClicked || dirtyFields.includes("lastName"))
    ) {
      return t("lastNameRequiredError");
    } else if (isNameLongEnough(lastName)) {
      return t("lastNameMinCharacterError");
    } else if (isNameInvalid(lastName)) {
      return t("noSpecialCharactersError");
    }
  }, [lastName, t, dirtyFields, saveClicked]);

  const middleNameError = useMemo(
    () => isNameInvalid(middleName),
    [middleName]
  );

  const birthdateError = useMemo(() => {
    if (birthdate) {
      const maxAge = dayjs("Jan 1 1901 00:00:00 GMT-00:00");
      const birthdateDayjs = getDateTimeWithFormat(
        birthdate,
        preferedDateFormat
      );

      const { month, day } = parseDateWithFormatting(
        birthdate,
        preferMonthDayYearFormat
      );

      if (
        !birthdateDayjs.isValid() ||
        birthdateDayjs.isAfter(dayjs()) ||
        birthdateDayjs.isBefore(maxAge) ||
        (minBirthdate && birthdateDayjs.isBefore(minBirthdate))
      ) {
        return t("enterAValidBirthDate");
      } else if (
        Number(month) !== birthdateDayjs.month() + 1 ||
        Number(day) !== birthdateDayjs.date()
      ) {
        // This check is necessary because dayjs automatically does modulo logic on input, for example:
        // 13/02/1944 will create a dayjs value of Jan 2nd, 1944
        return t("enterAValidBirthDate");
      }
    } else if (dirtyFields.includes("birthdate") || saveClicked) {
      return t("birthDateRequiredError");
    }
  }, [
    birthdate,
    preferMonthDayYearFormat,
    preferedDateFormat,
    minBirthdate,
    t,
    dirtyFields,
    saveClicked,
  ]);

  const hideKnownTravellerNumber = useMemo(() => {
    if (birthdateError) {
      return true;
    }

    if (birthdate) {
      const birthDateObj = dayjs(birthdate);
      const age = dayjs().diff(birthDateObj, "year");
      if (age < 2) {
        return true;
      }
    }

    return false;
  }, [birthdate]);

  const passportExpiryError = useMemo(() => {
    if (passport?.expiration) {
      const expiryDateDayjs = getDateTimeWithFormat(
        passport.expiration,
        preferedDateFormat
      );

      const { month, day } = preferMonthDayYearFormat
        ? parseMonthDayAndYear(passport.expiration)
        : parseDayMonthAndYear(passport.expiration);

      if (!expiryDateDayjs.isValid() || !expiryDateDayjs.isAfter(dayjs())) {
        return t("enterAValidExpiry");
      } else if (
        Number(month) !== expiryDateDayjs.month() + 1 ||
        Number(day) !== expiryDateDayjs.date()
      ) {
        return t("enterAValidExpiry");
      }
    }
  }, [passport, preferMonthDayYearFormat, preferedDateFormat, t]);

  const passportNumberError = useMemo(() => {
    if (passport?.number) {
      if (/\s/.test(passport.number)) {
        return t("noWhitespaceError");
      } else if (!specialCharacterRegex.test(passport.number)) {
        return t("noSpecialCharactersError");
      } else if (!passportRegex.test(passport.number)) {
        return t("invalidPassportNumber");
      }
    }
  }, [passport, t]);

  const getNationalIdError = useCallback(
    (nationalIdValue: string) => {
      if (
        nationalIdInfo?.regex &&
        !nationalIdInfo.regex.test(nationalIdValue)
      ) {
        return t("onlyNumbersPermittedError");
      } else if (nationalIdValue.length !== nationalIdInfo?.length?.min) {
        return t("nationalIdLengthError", {
          id: nationalIdInfo?.label,
          length: nationalIdInfo?.length?.formatted,
        });
      } else if (!nationalIdValue) {
        return t("nationalIdRequiredError", { id: nationalIdInfo?.label });
      }
      // Run additional checks, if they exist
      if (
        nationalIdInfo?.additionalValidation &&
        !nationalIdInfo.additionalValidation(
          nationalIdInfo?.formatPayload(nationalIdValue)
        )
      ) {
        return t("nationalIdInvalid", { id: nationalIdInfo?.label });
      }
      return "";
    },
    [nationalIdInfo, t]
  );

  const nationalIdError = useMemo(() => {
    if (
      !nationalIdInfo ||
      (!nationalId?.number &&
        nationalId?.number !== "" &&
        ((!saveClicked && ctaEnabledByDefault) || !ctaEnabledByDefault))
    )
      return;

    if (nationalId?.number) {
      return getNationalIdError(nationalId?.number);
    } else {
      return t("nationalIdRequiredError", { id: nationalIdInfo?.label });
    }
  }, [
    nationalId,
    t,
    nationalIdInfo,
    getNationalIdError,
    saveClicked,
    ctaEnabledByDefault,
  ]);

  const invalid =
    !(firstName?.length > 1) ||
    !(lastName?.length > 1) ||
    !birthdate ||
    !!birthdateError ||
    !!passportExpiryError ||
    !!passportNumberError ||
    !!firstNameError ||
    !!middleNameError ||
    !!lastNameError ||
    (nationalIdInfo && !nationalId) ||
    !!nationalIdError ||
    (!gender && !hideGenderField) ||
    showDriverLicenseSection
      ? isMissingDriverLicense
      : false;

  const isSaveDisabled = useMemo(
    () => (ctaEnabledByDefault ? false : invalid),
    [ctaEnabledByDefault, invalid]
  );

  const isPassportInfoValid = useCallback(
    (passport?: Passport) => {
      if (
        passport?.countryOfIssue !== "" &&
        passport?.expiration !== "" &&
        passport?.number !== ""
      ) {
        // Check if date is valid
        if (
          !getDateTimeWithFormat(passport.expiration, preferedDateFormat)
            .isValid
        ) {
          return false;
        }
        // Do we have number errors
        if (passportNumberError) {
          return false;
        }
        // Country of issue check is done if it's not empty, passport is valid
        return true;
      }
      return false;
    },
    [passportNumberError, preferedDateFormat]
  );

  const handleSaveTraveler = useCallback(() => {
    if (!saveClicked) {
      setSaveClicked(true);
    }

    if (isSaveDisabled) {
      return null;
    }

    if (invalid) {
      return null;
    }

    const onUpdate =
      isMobile && getEnvVariables("clientName") === ClientName.UBER;

    const isPassportValid = isPassportInfoValid(passport);
    if (isPassportValid) {
      // The passport's valid, format the date for the backend to be able to process
      passport.expiration = getDateTimeWithFormat(
        passport.expiration,
        preferedDateFormat
      ).format("YYYY-MM-DD");
    }

    const newTraveler: Person = {
      ...traveler,
      givenName: firstName.trim(),
      middleName: middleName.trim(),
      surname: lastName.trim(),
      dateOfBirth: getDateTimeWithFormat(birthdate, preferedDateFormat).format(
        "YYYY-MM-DD"
      ),
      nationality: nationality?.country ? nationality : null,
      passport: isPassportValid ? passport : undefined,
      gender: gender ? gender : Gender.F,
      frequentFlyer: getFrequentFlyerMap(frequentFlyerList),
      hotelLoyalty: getHotelLoyaltyMap(hotelLoyaltyList),
      assistance: assistance,
      id: traveler?.id || generateUserId(),
      createdAt: traveler?.createdAt || dayjs().toISOString(),
      nationalId: !nationalIdInfo
        ? null
        : {
            number: nationalIdInfo?.formatPayload
              ? nationalIdInfo.formatPayload(nationalId?.number)
              : nationalId?.number,
            expiration: nationalId?.expiration,
          },
    };

    if (knownTravelerNumber) {
      const birthDateObj = dayjs(birthdate);
      const age = dayjs().diff(birthDateObj, "year");
      if (age >= 2) {
        newTraveler.knownTravelerNumber = knownTravelerNumber;
      } else if (newTraveler.knownTravelerNumber) {
        // Delete KTN if traveler is an infant
        delete newTraveler.knownTravelerNumber;
      }
    }

    newTraveler.redressNumber =
      redressNumber && redressNumber.length > 0 ? redressNumber : undefined;

    newTraveler.driverLicense = driverLicense.street
      ? driverLicense
      : undefined;

    if (onUpdate) {
      handleUpdatePassenger(newTraveler, onUpdate, singleTravelerWorkflow);
    } else {
      handleUpdatePassenger(newTraveler, singleTravelerWorkflow);
    }

    return null;
  }, [
    saveClicked,
    isSaveDisabled,
    invalid,
    isMobile,
    isPassportInfoValid,
    passport,
    traveler,
    firstName,
    middleName,
    lastName,
    birthdate,
    preferedDateFormat,
    nationality,
    gender,
    frequentFlyerList,
    hotelLoyaltyList,
    assistance,
    nationalIdInfo,
    nationalId?.number,
    nationalId?.expiration,
    knownTravelerNumber,
    redressNumber,
    driverLicense,
    handleUpdatePassenger,
    singleTravelerWorkflow,
  ]);

  const onBirthdateChanged = useCallback(
    (date: string) => {
      const { month, day, year, strLength } = preferMonthDayYearFormat
        ? parseMonthDayAndYear(date)
        : parseDayMonthAndYear(date);

      const formattedBirthdate = preferMonthDayYearFormat
        ? month +
          (strLength > 2 ? "/" : "") +
          day +
          (strLength > 4 ? "/" : "") +
          year
        : day +
          (strLength > 2 ? "/" : "") +
          month +
          (strLength > 4 ? "/" : "") +
          year;

      setBirthdate(formattedBirthdate);
    },
    [preferMonthDayYearFormat]
  );

  const onPassportExpiryChanged = useCallback(
    (passportExpiry: string) => {
      const { month, day, year, strLength } = preferMonthDayYearFormat
        ? parseMonthDayAndYear(passportExpiry)
        : parseDayMonthAndYear(passportExpiry);

      const formattedPassportExpiry = preferMonthDayYearFormat
        ? month +
          (strLength > 2 ? "/" : "") +
          day +
          (strLength > 4 ? "/" : "") +
          year
        : day +
          (strLength > 2 ? "/" : "") +
          month +
          (strLength > 4 ? "/" : "") +
          year;

      setPassport({
        ...passport,
        expiration: formattedPassportExpiry,
      });
    },
    [passport, preferMonthDayYearFormat]
  );

  const genderOptionsWithTranslations = useMemo(() => {
    const genderMap = {
      [Gender.M]: t("person_male"),
      [Gender.F]: t("person_female"),
    };

    return [...genderOptions].map((option) => {
      option.label = genderMap[option.value];
      return option;
    });
  }, [t]);

  const getGenderTranslations = useCallback(() => {
    return renderMenu(genderOptionsWithTranslations);
  }, [genderOptionsWithTranslations]);

  const onInputFocusHandler = useCallback(
    async (
      id: string,
      event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      // When scroll behavior is "none", skip ALL custom handling including state changes
      // This prevents any re-renders during focus that could cause cursor positioning bugs
      // on iOS Safari in iframe contexts
      if (scrollOnFocusBehavior === "none") {
        return;
      }

      if (setIsInputFocused) {
        setIsInputFocused(true);
      }

      const travelerInfoFormPopupElement = document
        ?.getElementsByClassName("traveler-select-workflow-info-form-popup")[0]
        ?.getElementsByClassName(
          "mobile-popover-card-container"
        )?.[0]?.parentElement;
      const inputElement = document.getElementById(id);
      const inputTop = inputElement?.getBoundingClientRect().top || 0;
      const viewportHeight = window.innerHeight;
      const desiredTopPosition = 48; // 3rem padding from top

      if (isMobile) {
        // Only scroll if the input is not already visible in a good position
        // Consider an input "visible" if it's in the top 60% of the viewport
        const isInputVisible =
          inputTop >= desiredTopPosition && inputTop < viewportHeight * 0.6;

        if (isInputVisible) {
          // Input is already in a good position, no need to scroll
          return;
        }

        if (travelerInfoFormPopupElement) {
          // Scroll the popup container to bring the input into view
          inputElement?.scrollIntoView({
            behavior: scrollOnFocusBehavior as ScrollBehavior,
            block: "center",
          });
        } else if (!travelerInfoFormPopupElement) {
          // The info form is not in a popup, scroll the window element
          // Use scrollTo with absolute position instead of scrollBy with relative offset
          const scrollTarget = window.scrollY + inputTop - desiredTopPosition;
          window.scrollTo({
            top: scrollTarget,
            behavior: scrollOnFocusBehavior as ScrollBehavior,
          });
        }
      }
    },
    [isMobile, setIsInputFocused, scrollOnFocusBehavior]
  );

  const birthdateLabel = useMemo(
    () => (
      <I18nMarkup
        tKey={"birthDateWithDateFormat"}
        replacements={{
          format: preferedDateFormat.toLowerCase(),
          interpolation: { escapeValue: false },
        }}
      />
    ),
    [preferedDateFormat]
  );

  const passportExpiryLabel = useMemo(
    () =>
      t("expiration", {
        format: preferedDateFormat,
        interpolation: { escapeValue: false },
      }),
    [preferedDateFormat, t]
  );

  const handleNationalIdChange = useCallback(
    (value) => {
      if (!value) {
        getNationalIdError("");
      }
      const formattedValue = nationalIdInfo?.onChange(
        value,
        nationalId?.number?.length - value.length < 0
      );
      setNationalId({
        number: formattedValue,
        expiration: nationalId?.expiration,
      });
    },
    [
      nationalId?.number,
      nationalId?.expiration,
      nationalIdInfo,
      getNationalIdError,
    ]
  );

  const handleFirstNameChange = useCallback(
    (newFirstName: string) => setFirstName(newFirstName),
    [setFirstName]
  );

  const handleMiddleNameChange = useCallback(
    (newMiddleName: string) => setMiddleName(newMiddleName),
    [setMiddleName]
  );

  const handleLastNameChange = useCallback(
    (newLastName: string) => setLastName(newLastName),
    [setLastName]
  );

  const handlePassportChange = useCallback(
    (newPassport: string) => {
      setPassport({
        ...passport,
        number: newPassport,
      });
    },
    [setPassport, passport]
  );

  const handleGenderChange = useCallback(
    (newGender: string) => setGender(newGender as Gender),
    [setGender]
  );

  const handleCountryChange = useCallback(
    (newCountry: string) => {
      setPassport({
        ...passport,
        countryOfIssue: newCountry,
      });
    },
    [setPassport, passport]
  );

  const countryOptions = useMemo(() => {
    const options = Array.from(Object.entries(getAllCountryNames())).map(
      ([code, label]) => {
        return {
          label,
          value: code,
          icon: (
            <span className="checkout-country-option-flag-icon">
              {emojiFlags.countryCode(code).emoji}
            </span>
          ),
        };
      }
    );
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [getAllCountryNames]);

  const getNationalityOptions = useCallback(
    (preferredCountry: string) => [
      {
        label: preferredCountry ? getCountryName(preferredCountry) : "",
        value: preferredCountry,
      },
      ...countryOptions
        .filter((option) => option.value !== preferredCountry)
        .sort((a, b) => (a.label < b.label ? -1 : b.label > a.label ? 1 : 0)),
    ],
    [getCountryName, countryOptions]
  );

  return (
    <div
      className={clsx(
        "traveler-info-form-root",
        { mobile: isMobile },
        className,
        buttonClassName
      )}
    >
      <div
        className={clsx("traveler-info-form-container", {
          edit: traveler?.id,
        })}
      >
        <div
          className={clsx("traveler-info-form-section", "basic-info-section")}
        >
          {!hideHeader && (
            <div className="traveler-info-description">
              <Typography variant="h6">{travelerInfoTitle}</Typography>
              <Typography variant="body2">{travelerInfoSubtitle}</Typography>
            </div>
          )}
          <form className="traveler-info-form">
            <TextField
              aria-label={t("firstName")}
              aria-live="assertive"
              label={t("firstName")}
              className={clsx("traveler-info-field", "first-name", {
                error: firstNameError,
              })}
              required
              value={firstName}
              error={
                !!firstNameError ||
                (firstName && (firstName.length <= 1 || firstName.length > 32))
              }
              helperText={firstNameError}
              inputProps={{ minLength: 2 }}
              onChange={handleFirstNameChange}
              onBlur={() => {
                resetIsInputFocused();
                if (!dirtyFields.includes("firstName")) {
                  setDirtyFields([...dirtyFields, "firstName"]);
                }
              }}
              onFocus={(_value, event) =>
                onInputFocusHandler("first-name-input", event)
              }
              id="first-name-input"
              variant="default"
            />
            <TextField
              aria-label={t("middleNameOptional")}
              aria-live="assertive"
              label={t("middleNameOptional")}
              className={clsx("traveler-info-field", "middle-name", {
                error: middleName && middleNameError,
              })}
              value={middleName}
              error={!!middleName && !!middleNameError}
              helperText={middleNameError && t("noSpecialCharactersError")}
              onChange={handleMiddleNameChange}
              onFocus={(_value, event) =>
                onInputFocusHandler("middle-name-input", event)
              }
              onBlur={resetIsInputFocused}
              id="middle-name-input"
              variant="default"
            />
            <TextField
              aria-label={t("lastName")}
              aria-live="assertive"
              label={t("lastName")}
              className={clsx("traveler-info-field", "last-name", {
                error: lastNameError,
              })}
              required
              value={lastName}
              error={!!lastNameError || (lastName && lastName.length <= 1)}
              helperText={lastNameError}
              inputProps={{ minLength: 2 }}
              onChange={handleLastNameChange}
              onBlur={() => {
                resetIsInputFocused();
                if (!dirtyFields.includes("lastName")) {
                  setDirtyFields([...dirtyFields, "lastName"]);
                }
              }}
              onFocus={(_value, event) =>
                onInputFocusHandler("last-name-input", event)
              }
              id="last-name-input"
              variant="default"
            />
            <TextField
              aria-live="assertive"
              aria-label={`${t(
                "birthDate"
              )} ${preferedDateFormat.toLowerCase()}`}
              label={birthdateLabel}
              className={clsx("traveler-info-field", "birthdate", {
                error: !!birthdateError,
              })}
              required
              value={birthdate}
              error={!!birthdateError}
              helperText={birthdateError}
              onChange={onBirthdateChanged}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Backspace") {
                  const target = e.target as HTMLInputElement;
                  if (target.value[target.selectionStart - 1] === "/") {
                    if (target.selectionStart === target.value.length) {
                      // Delete "/" and previous char
                      setBirthdate(
                        target.value.substring(0, target.selectionStart - 1)
                      );
                    } else {
                      e.preventDefault();
                      // Delete previous char, keep "/"
                      const newBirthdate =
                        target.value.slice(0, target.selectionStart - 2) +
                        target.value.slice(
                          target.selectionStart - 1,
                          target.value.length
                        );
                      setBirthdate(newBirthdate);
                    }
                  }
                }
              }}
              onBlur={() => {
                resetIsInputFocused();
                if (!dirtyFields.includes("birthdate")) {
                  setDirtyFields([...dirtyFields, "birthdate"]);
                }
              }}
              type="tel"
              inputProps={{
                pattern: "[0-9]*",
              }}
              onFocus={(_value, event) =>
                onInputFocusHandler("birthday-input", event)
              }
              id="birthday-input"
              placeholder={preferedDateFormat}
              variant="default"
            />
            {!hideGenderField && (
              <Slot
                id="add-traveler-form-select"
                options={genderOptionsWithTranslations}
                label={t("gender")}
                placeholder={"Select gender"}
                setOption={(option) => setGender(option as Gender)}
                selected={genderOptionsWithTranslations?.find(
                  (option) => option.value === gender
                )}
                required
                className={clsx("traveler-info-field", "gender")}
                error={genderError}
                helperText={genderError}
                data-dd-privacy="mask"
                component={
                  <B2BTextField
                    label={t("gender")}
                    aria-label={t("gender")}
                    className={clsx("traveler-info-field", "gender")}
                    required
                    select
                    value={gender}
                    onChange={handleGenderChange}
                    id="gender-input"
                    variant="default"
                    data-dd-privacy="mask"
                  >
                    {getGenderTranslations()}
                  </B2BTextField>
                }
              />
            )}
            {!hideNationalityField && (
              <Slot
                id="add-traveler-form-select"
                options={getNationalityOptions(
                  brand?.preferredCountryCode || "US"
                )}
                label={t("nationality")}
                setOption={(option: RadioOption["value"]) => {
                  setNationality({
                    country: option?.toString() || "",
                  });
                }}
                selected={{
                  value: nationality?.country || "",
                  label: nationality?.country
                    ? getCountryName(nationality?.country)
                    : "",
                }}
                className={clsx("traveler-info-field", "nationality")}
                required={nationalityRequired}
                error={nationalityError}
                component={
                  <Autocomplete
                    fullWidth
                    value={{
                      value: nationality?.country || "",
                      label: nationality?.country
                        ? getCountryName(nationality?.country)
                        : "",
                    }}
                    classes={{ paper: "nationality-autocomplete-results" }}
                    className="nationality-autocomplete"
                    options={getNationalityOptions(
                      brand.preferredCountryCode || "US"
                    )}
                    onChange={(_e, option: any) =>
                      setNationality({
                        country: option?.value.toString() || "",
                      })
                    }
                    getOptionLabel={(option) =>
                      option.value ? getCountryName(option.value) : ""
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        aria-label={t("nationality")}
                        label={t("nationality")}
                        className={clsx("traveler-info-field", "nationality")}
                        id="nationality-input"
                        variant="default"
                        onBlur={resetIsInputFocused}
                      />
                    )}
                  />
                }
              />
            )}
            {nationalIdInfo ? (
              <TextField
                aria-label={nationalIdInfo.label}
                aria-live="assertive"
                label={nationalIdInfo.label}
                className={clsx("traveler-info-field", "national-id", {
                  error: nationalIdError,
                })}
                required
                value={nationalId?.number}
                error={!!nationalIdError || nationalId?.number?.length <= 1}
                helperText={nationalIdError}
                inputProps={
                  nationalIdInfo.length
                    ? {
                        minLength: nationalIdInfo.length?.min,
                        maxLength: nationalIdInfo.length?.max,
                      }
                    : null
                }
                onChange={handleNationalIdChange}
                onFocus={(_value, event) =>
                  onInputFocusHandler("national-id-input", event)
                }
                onBlur={resetIsInputFocused}
                id="national-id-input"
                placeholder={t("nationalIdPlaceholder", {
                  id: nationalIdInfo.label,
                })}
                variant="default"
              />
            ) : null}
          </form>
        </div>
        {!hideHotelLoyaltySection && (
          <HotelLoyaltySection
            hotelLoyaltyTitle="Hotel Loyalty"
            hotelLoyaltyList={hotelLoyaltyList}
            setHotelLoyaltyList={setHotelLoyaltyList}
          />
        )}
        {!hideFrequentFlyerSection && (
          <FrequentFlyerSection
            className="traveler-info-form-section"
            frequentFlyerTitle={frequentFlyerTitle}
            frequentFlyerList={frequentFlyerList}
            setFrequentFlyerList={setFrequentFlyerList}
            isMobile={isMobile}
            closeButton={closeButton}
            isJetBlueTenant={isJetBlueTenant}
            handleOnBlur={resetIsInputFocused}
            handleOnFocus={() => setIsInputFocused(true)}
          />
        )}
        {!hideAdditionalInfoSection && (
          <AdditionalInformationSection
            className="traveler-info-form-section"
            additionalInfoTitle={additionalInfoTitle}
            assistance={assistance}
            setAssistance={setAssistance}
            redressNumber={redressNumber}
            setRedressNumber={setRedressNumber}
            knownTravelerNumber={knownTravelerNumber}
            setKnownTravelerNumber={setKnownTravelerNumber}
            handleOnBlur={resetIsInputFocused}
            handleOnFocus={() => setIsInputFocused(true)}
            hideKnownTravellerNumber={hideKnownTravellerNumber}
          />
        )}
        {showDriverLicenseSection ? (
          <DriverLicenseSection
            driverLicenseSectionTitle="Driver's License Details"
            className="traveler-info-form-section"
            driverLicense={driverLicense}
            isMissingDriverLicense={isMissingDriverLicense}
            onSetLicense={(license) => setLicense(license)}
            isMobile={isMobile}
          />
        ) : null}
        {showPassport || (traveler?.passport && !hideFrequentFlyerSection) ? (
          <div
            className={clsx(
              "passport-info-container",
              "traveler-info-form-section"
            )}
          >
            <div className="traveler-info-description">
              <Typography variant="subtitle1">
                {passportTitle || t("passportInformation")}
              </Typography>
            </div>
            {passportSubtitle && (
              <div className="traveler-info-subtitle">
                <Typography variant="subtitle1">{passportSubtitle}</Typography>
              </div>
            )}
            <form className={clsx("passport-info-form", "traveler-info-form")}>
              <Slot
                id="passenger-passport-country-select"
                options={countryOptions}
                setOption={(value: string) => {
                  setPassport({
                    ...passport,
                    countryOfIssue: value,
                  });
                }}
                selected={passport.countryOfIssue}
                label={t("countryOfIssue")}
                component={
                  <B2BTextField
                    label={t("countryOfIssue")}
                    className={clsx("traveler-info-field", "country-of-issue")}
                    required
                    select
                    value={passport.countryOfIssue}
                    onBlur={resetIsInputFocused}
                    onChange={handleCountryChange}
                    id="passport-country-of-issue-input"
                    InputProps={{ id: "passport-country-of-issue-input" }}
                    variant="default"
                  >
                    {renderMenu(countryOptions)}
                  </B2BTextField>
                }
              />
              <TextField
                label={t("passportNumber")}
                className={clsx("traveler-info-field", "passport-number", {
                  error: !!passportNumberError,
                })}
                value={passport.number}
                onChange={handlePassportChange}
                onFocus={(_value, event) =>
                  onInputFocusHandler("passport-number-input", event)
                }
                onBlur={resetIsInputFocused}
                id="passport-number-input"
                InputProps={{ id: "passport-number-input" }}
                error={!!passportNumberError}
                helperText={passportNumberError}
                variant="default"
              />
              <TextField
                label={passportExpiryLabel}
                className={clsx("traveler-info-field", "passport-expiration", {
                  error: !!passportExpiryError,
                })}
                value={passport.expiration}
                onChange={onPassportExpiryChanged}
                onFocus={(_value, event) =>
                  onInputFocusHandler("passport-expiration-input", event)
                }
                onBlur={resetIsInputFocused}
                id="passport-expiration-input"
                InputProps={{ id: "passport-expiration-input" }}
                error={!!passportExpiryError}
                helperText={passportExpiryError}
                variant="default"
              />
            </form>
          </div>
        ) : null}
        {!isMobile ? (
          <Slot
            id="desktop-traveler-edit-form-buttons"
            onClose={handleClose}
            onSave={handleSaveTraveler}
            saveDisabled={isSaveDisabled}
            component={
              <div className="traveler-info-form-button-container">
                <ActionButton
                  className={clsx(
                    "traveler-info-button",
                    "traveler-save",
                    buttonClassName
                  )}
                  message={t("saveTraveler")}
                  disabled={isSaveDisabled}
                  onClick={handleSaveTraveler}
                  defaultStyle="h4r-primary"
                />
                <ActionButton
                  className={clsx("traveler-info-button", "traveler-cancel", {
                    hideButton: !handleClose,
                  })}
                  message={t("cancel")}
                  onClick={handleClose}
                  defaultStyle="h4r-secondary"
                />
              </div>
            }
          />
        ) : (
          <Slot
            id="mobile-traveler-edit-form-buttons"
            travelerId={traveler?.id}
            disabled={isSaveDisabled}
            onSave={handleSaveTraveler}
            onDelete={handleRemovePassenger}
            component={
              <MobileTravelerFormButtons
                className={clsx({ "focused-within-form": isInputFocused })}
                buttonClassName={buttonClassName}
                travelerId={traveler?.id}
                disabled={isSaveDisabled}
                onSave={handleSaveTraveler}
                onDelete={handleRemovePassenger}
              />
            }
          />
        )}
      </div>
    </div>
  );
};

type MobileTravelerFormButtonsProps = {
  className?: string;
  buttonClassName?: string;
  travelerId?: string;
  isEdit?: boolean;
  disabled: boolean;
  onSave: () => void;
  onDelete: (travelerId: string) => void;
};

const MobileTravelerFormButtons = ({
  className,
  buttonClassName,
  travelerId,
  disabled,
  onSave,
  onDelete,
}: MobileTravelerFormButtonsProps) => {
  const { t } = useI18nContext();

  return (
    <div
      className={clsx(
        "floating-box traveler-info-form-docked-buttons-container",
        {
          hidden: disabled,
        },
        className
      )}
    >
      <MobileFloatingSection
        className="traveler-info-form-button-container"
        primaryButton={{
          children: travelerId ? t("saveChanges") : t("savePassenger"),
          onClick: onSave,
          className: "mobile-traveler-info-form-save-button",
          wrapperClassName: clsx(
            "mobile-traveler-info-form-save-button-container",
            buttonClassName
          ),
          disabled,
        }}
        secondaryButton={
          travelerId
            ? {
                children: t("deletePassenger"),
                onClick: () => onDelete(travelerId),
                className: "traveler-info-form-delete-button",
                wrapperClassName: "traveler-info-form-section delete",
              }
            : null
        }
      />
    </div>
  );
};
